// src/components/common/LatestTweetsCard.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DynamicIcon } from "lucide-react/dynamic";
import xLogo from "@/assets/x_logo_16.png";
import { useFetchTweets } from "@/hooks/twitter/useFetchTweets";
import type { Tweet } from "@/types/mediaInsights/twitter";

type TweetSentiment = "bullish" | "bearish" | "neutral";


function timeAgo(iso: string): string {
  const deltaMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(deltaMs / 60000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function buildTokenRoute(symbol: string): string {
  return `/app/tokens/${symbol.toLowerCase()}`;
}

function SentimentBadge({ sentiment }: { sentiment: TweetSentiment }) {
  const color =
    sentiment === "bullish"
      ? "text-emerald-400 bg-emerald-400/10"
      : sentiment === "bearish"
      ? "text-rose-400 bg-rose-400/10"
      : "text-slate-300 bg-slate-600/20";
  const label = sentiment.toUpperCase();
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] tracking-wide font-semibold ${color}`}>
      {label}
    </span>
  );
}

function HighlightedContent({
  text,
  onTokenClick,
}: {
  text: string;
  onTokenClick: (symbol: string) => void;
}) {
  const parts = useMemo(() => {
    const regex = /(\$[A-Z0-9]{2,10})/g;
    const segments: Array<{ type: "text" | "token"; value: string }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex)
        segments.push({
          type: "text",
          value: text.slice(lastIndex, match.index),
        });
      const group = match[1] ?? "";
      if (group) {
        segments.push({ type: "token", value: group.slice(1).toUpperCase() });
        lastIndex = match.index + group.length;
      } else {
        lastIndex = match.index;
      }
    }
    if (lastIndex < text.length)
      segments.push({ type: "text", value: text.slice(lastIndex) });
    return segments;
  }, [text]);

  return (
    <p className="text-sm text-slate-200 leading-relaxed">
      {parts.map((p, i) =>
        p.type === "text" ? (
          <span key={`t-${i}`}>{p.value}</span>
        ) : (
          <button
            key={`k-${i}`}
            onClick={() => onTokenClick(p.value)}
            className="text-blue-400 hover:text-blue-300 font-medium"
            title={`Open ${p.value} dashboard`}>
            ${p.value}
          </button>
        )
      )}
    </p>
  );
}

interface Props {
  fullHeight?: boolean;
  ticker?: string;
  tickers?: string[];
  showHeader?: boolean;
  showSentimentFilters?: boolean;
}

const LatestTweetsCard: React.FC<Props> = ({
  fullHeight = false,
  ticker,
  tickers,
  showHeader = true,
  showSentimentFilters = true,
}) => {
  const navigate = useNavigate();
  const [sentimentFilter, setSentimentFilter] = useState<
    "all" | TweetSentiment
  >("all");
  const upperTickers = useMemo(() => {
    const base =
      Array.isArray(tickers) && tickers.length > 0
        ? tickers
        : ticker
        ? [ticker]
        : [];
    const uniq = Array.from(
      new Set(
        base
          .map((t) => String(t || "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
    return uniq;
  }, [tickers, ticker]);

  // Always fetch tweets - if upperTickers is empty, fetch all tweets
  const isLiveMode = true; // Always enabled now - can show filtered or all tweets
  const { 
    tweets: liveTweets, 
    isLoading: isLiveLoading, 
    isInitialLoading: isLiveInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchTweets({
    ...(upperTickers.length > 0 ? { tickers: upperTickers } : {}),
    infinite: true,
    pageSize: 50,
    sort_by: "created_at",
    sort_order: "desc",
  });

  const deriveSentimentFromStance = useCallback((tweet: Tweet): TweetSentiment => {
    const entities = Array.isArray(tweet.entities) ? tweet.entities : [];
    if (entities.length === 0) return "neutral";
    // Prefer stance for any requested ticker if available
    let stanceSource: string | null | undefined = undefined;
    if (upperTickers.length > 0) {
      const match = entities.find((e) =>
        upperTickers.includes((e.entity_ticker || "").toUpperCase())
      );
      stanceSource = match?.stance ?? undefined;
    }
    const stance = (stanceSource ?? entities[0]?.stance ?? "").toLowerCase();
    if (stance === "bullish") return "bullish";
    if (stance === "bearish") return "bearish";
    return "neutral";
  }, [upperTickers]);

  const filteredLiveTweets = useMemo(() => {
    if (!isLiveMode) return [];
    if (!showSentimentFilters) return liveTweets;
    if (sentimentFilter === "all") return liveTweets;
    return liveTweets.filter((t) => deriveSentimentFromStance(t) === sentimentFilter);
  }, [isLiveMode, liveTweets, sentimentFilter, deriveSentimentFromStance, showSentimentFilters]);
  const sortedLiveTweets = useMemo(() => {
    // Tweets are already sorted by API (sort_order: "desc"), but we still need to sort
    // the filtered results since client-side filtering might change order
    const arr = [...filteredLiveTweets];
    arr.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return arr;
  }, [filteredLiveTweets]);

  const containerClass = fullHeight
    ? "p-4 sm:p-5 flex flex-col min-h-0 h-full"
    : "rounded-xl bg-gray-800/40 p-4 sm:p-5";
  const listClass = fullHeight
    ? "space-y-4 flex-1 min-h-0 overflow-y-auto pr-2"
    : "space-y-4 h-[580px] overflow-y-auto pr-2";
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  // Infinite scroll using IntersectionObserver on the sentinel
  React.useEffect(() => {
    const rootEl = listRef.current;
    const sentinelEl = sentinelRef.current;
    if (!rootEl || !sentinelEl || !fetchNextPage) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            entry.isIntersecting &&
            isLiveMode &&
            hasNextPage &&
            !isLiveLoading &&
            !isFetchingNextPage &&
            fetchNextPage
          ) {
            fetchNextPage();
          }
        }
      },
      { root: rootEl, rootMargin: "100px", threshold: 0.1 }
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [isLiveMode, hasNextPage, isLiveLoading, isFetchingNextPage, fetchNextPage]);

  return (
    <div className={containerClass}>
      {showHeader && (
        <div className="flex items-center justify-between mb-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <img src={xLogo} alt="X" className="w-4 h-4 opacity-80" />
            <div className="text-xs uppercase tracking-wide text-slate-300">
              Tweets
            </div>
          </div>
          {isLiveMode && showSentimentFilters && (
            <div className="flex items-center gap-1 bg-gray-800/50 rounded-lg p-1 border border-gray-700/50">
              {(["all", "bullish", "bearish", "neutral"] as const).map((key) => (
                <button
                  key={key}
                  onClick={() => setSentimentFilter(key)}
                  className={`px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors ${
                    sentimentFilter === key
                      ? "bg-slate-600/30 text-slate-200"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/30"
                  }`}
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div ref={listRef} className={listClass}>
        {/* IntersectionObserver sentinel is inside the scrollable list as the last element */}
        {isLiveMode ? (
          <>
            {isLiveInitialLoading && (
              <div className="space-y-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <article
                    key={`tw-skel-${i}`}
                    className="rounded-lg bg-gray-800/40 p-3 sm:p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-700 animate-pulse" />
                      <div className="flex-1 min-w-0">
                        <div className="h-3.5 w-40 bg-gray-700 rounded animate-pulse" />
                        <div className="mt-2 h-3 w-3/4 bg-gray-700 rounded animate-pulse" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {sortedLiveTweets.map((tw) => {
                const t = tw as Tweet;
                const stanceSentiment = deriveSentimentFromStance(t);
                return (
                  <div
                    key={t.id}
                    className="rounded-lg bg-gray-800/40 p-3 sm:p-4 transition">
                    <div className="flex items-start gap-3">
                      {t.author?.profile_picture ? (
                        <img
                          src={t.author.profile_picture}
                          alt={t.author?.username || "author"}
                          className="w-8 h-8 rounded-full ring-1 ring-gray-700/60"
                          onError={(e) =>
                            ((e.target as HTMLImageElement).style.display =
                              "none")
                          }
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-700/70 ring-1 ring-gray-700/60 flex items-center justify-center text-xs text-slate-200">
                          {(t.author?.name || t.author?.username || "?")
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-slate-200 font-medium text-sm">
                            {t.author?.name || t.author?.username || "Unknown"}
                          </div>
                          {t.author?.username && (
                            <div className="text-xs text-slate-400 truncate">
                              @{t.author.username} · {timeAgo(t.created_at)}
                            </div>
                          )}
                          <div className="ml-auto flex items-center gap-2">
                            <SentimentBadge sentiment={stanceSentiment} />
                            {t.url && (
                              <a
                                href={t.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200"
                                title="Open tweet">
                                <DynamicIcon name="external-link" className="w-3.5 h-3.5" />
                                View
                              </a>
                            )}
                          </div>
                        </div>
                        {t.content && (
                          <div className="mt-2">
                            <HighlightedContent
                              text={t.content ?? ""}
                              onTokenClick={(sym) =>
                                navigate(buildTokenRoute(sym))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            {!isLiveLoading && sortedLiveTweets.length === 0 && (
              <div className="rounded-lg border border-gray-700/40 bg-gray-900/30 p-6 text-center text-sm text-slate-400">
                No tweets available.
              </div>
            )}
            {/* Bottom inline loader during pagination fetch */}
            {isFetchingNextPage && (
              <div className="pt-2 pb-3">
                <article className="rounded-lg bg-gray-800/40 p-3 sm:p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-700 animate-pulse" />
                    <div className="flex-1 min-w-0">
                      <div className="h-3.5 w-40 bg-gray-700 rounded animate-pulse" />
                      <div className="mt-2 h-3 w-3/4 bg-gray-700 rounded animate-pulse" />
                    </div>
                  </div>
                </article>
              </div>
            )}
            {/* End of list indicator */}
            {!hasNextPage && sortedLiveTweets.length > 0 && !isLiveLoading && (
              <div className="text-center text-xs text-gray-500 py-2">
                No more tweets
              </div>
            )}
            <div ref={sentinelRef} className="h-1" />
          </>
        ) : (
          <div className="rounded-lg border border-gray-700/40 bg-gray-900/30 p-6 text-center text-sm text-slate-400">
            No tweets available.
          </div>
        )}
      </div>
    </div>
  );
};

export default LatestTweetsCard;
